import {
  GoABadge,
  GoABlock,
  GoAButton,
  GoAContainer,
  GoASpacer,
} from '@abgov/react-components';
import React, { useEffect } from 'react';
import { useIsDesktop } from '../../utils/isDesktop';
import { useNavigate } from 'react-router-dom';
import { FormStep, getFirstIncompleteStep } from '../../models/collision';
import { isHitAndRun } from '../../state/slices/configurationSlice';
import { REACT_COLLISION_REPORT_SYSTEM_URL } from '../../state/configuration';
import { RootState } from '../../state/store';
import { useSelector } from 'react-redux';

interface CollisionReportComponentProps {
  isArchived: boolean;
  reportId?: number;
  createdDate?: string;
  lastUpdatedDate?: string;
  reportStatus?: number;
  policeFileNumber?: string;
  steps?: FormStep[];
  collisionType?: number;
}

export const CollisionReportComponent = ({
  isArchived,
  reportId,
  createdDate,
  lastUpdatedDate,
  reportStatus,
  policeFileNumber,
  steps,
  collisionType,
}: CollisionReportComponentProps) => {
  const useAppSelector: <TSelected>(
    selector: (state: RootState) => TSelected,
  ) => TSelected = useSelector;

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const reportStatusInProgress = 1;
  const reportStatusSubmitted = 2;
  const reportStatusUnderReview = 3;
  const reportStatusApproved = 4;
  const reportStatusRejected = 5;
  const submittedReportText =
    "Your report is awaiting law enforcement review. Once it's under review, the status will be updated on the dashboard.";
  const reviewReportText =
    'Your report is currently being reviewed. The status will be updated on the dashboard once the review process is completed by law enforcement.';
  const approvedReportText =
    'Click on ‘Access report’ to request a copy of your approved report from the Online Collision Report System. To request your report, use the police file number provided on your damage sticker';
  const rejectedReportText =
    'Contact or visit the detachment for more information on your rejected report.';
  const archivedReportText =
    "This collision report has been archived. Click on 'Access report' to request a copy" +
    ' of your approved report from the Online Collision Report System. To request your report, use the assigned police file number: ' +
    policeFileNumber;
  const inProgressReportText =
    'Resume your report or view a summary to track your progress or make any changes to the saved information.';

  const reportDetailsHeaderText = 'Report details';
  const reportDetailsHeaderTextWhenSubmitted = 'Report heading lorum ipsum';

  const isDesktop = useIsDesktop();
  const isReportApproved = reportStatus === reportStatusApproved; //TODO: Replace with enum
  const navigateToReviewStep = '/collision#review';

  const formatDate = (date?: string) => {
    if (!date) return 'Invalid date';

    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    return 'Invalid date';
  };

  const handleResumeButtonClick = () => {
    const isHitAndRunCollision = isHitAndRun(collisionType ?? 0, stateConfiguration);
    const firstIncompleteStep = getFirstIncompleteStep(steps, isHitAndRunCollision);
    if (firstIncompleteStep) {
      navigate(`/collision${firstIncompleteStep.nav}`);
    } else {
      navigate(navigateToReviewStep);
    }
  };

  const handleAccessReportClick = () => {
    if (REACT_COLLISION_REPORT_SYSTEM_URL) {
      window.open(
        REACT_COLLISION_REPORT_SYSTEM_URL,
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  const getFilteredStepsInfo = (
    steps: FormStep[],
    collisionType: number,
    stateConfiguration: any,
  ): { completedSteps: number; totalSteps: number } => {
    const filteredSteps = isHitAndRun(collisionType, stateConfiguration)
      ? steps.filter(
          (step) => step.step !== 7 && step.step !== 9 && step.step !== 10,
        )
      : steps.filter((step) => step.step !== 10);

    const completedSteps = filteredSteps.filter(
      (step) => step.completed,
    ).length;
    const totalSteps = filteredSteps.length;

    return { completedSteps, totalSteps };
  };

  const getStepStatusText = (
    steps: FormStep[] | undefined,
    collisionType: number,
    stateConfiguration: any,
  ): string => {
    if (steps) {
      const { completedSteps, totalSteps } = getFilteredStepsInfo(
        steps,
        collisionType,
        stateConfiguration,
      );

      console.log('completedSteps', completedSteps);
      console.log('totalSteps', totalSteps);

      return `${completedSteps} of ${totalSteps} steps completed`;
    }
    return '';
  };

  const navigate = useNavigate();

  useEffect(() => {
    console.log('reportStatus', reportStatus);
    console.log('collisionType', collisionType);
  }, []);

  return (
    <div className="dashboard-report-container">
      <h4 style={{ marginBottom: '12px' }}>
        {isArchived ? 'Archived report' : 'Report in progress'}
      </h4>
      <GoAContainer accent="filled" width="full">
        <GoABlock
          gap="l"
          direction={isDesktop ? 'row' : 'column'}
          alignment="start"
          mt="l"
          mb="l"
          mr={isDesktop ? 'm' : 'none'}
          ml={isDesktop ? 'm' : 'none'}
        >
          <div style={{ minWidth: '278px' }}>
            <GoABadge
              content={`Report ID# ${reportId}`}
              type="information"
            ></GoABadge>
            <GoASpacer vSpacing="xs"></GoASpacer>
            <span className="dashboard-report-start-date">
              Report started: {formatDate(createdDate)}
            </span>
            <GoASpacer vSpacing="none"></GoASpacer>
            <span className="dashboard-report-update-date">
              {isReportApproved ||
              reportStatus === reportStatusSubmitted ||
              reportStatus === reportStatusUnderReview
                ? `Submission date: ${formatDate(lastUpdatedDate)}`
                : `Last updated: ${formatDate(lastUpdatedDate)}`}
            </span>
          </div>
          <div className="dashboard-report-details-divider"></div>
          {/* Container for details section with badge positioning */}

          <div className="dashboard-report-details-badge-container">
            <GoABadge
              type={
                reportStatus === reportStatusInProgress
                  ? 'light'
                  : reportStatus === reportStatusRejected
                    ? 'emergency'
                    : 'success'
              }
              content={
                reportStatus === reportStatusInProgress
                  ? collisionType !== undefined
                    ? getStepStatusText(
                        steps,
                        collisionType,
                        stateConfiguration,
                      )
                    : ''
                  : reportStatus === reportStatusUnderReview ||
                      reportStatus === reportStatusSubmitted
                    ? 'Submitted'
                    : reportStatus === reportStatusRejected
                      ? 'Rejected'
                      : 'Approved'
              }
            />
          </div>

          <div className="dashboard-report-details-container">
            <span className="dashboard-report-details-header">
              {reportStatus === reportStatusSubmitted
                ? reportDetailsHeaderText
                : reportDetailsHeaderText}
            </span>
            <GoASpacer vSpacing="none"></GoASpacer>
            <span className="dashboard-report-details-text">
              {isArchived
                ? archivedReportText
                : isReportApproved
                  ? approvedReportText
                  : reportStatus === reportStatusSubmitted
                    ? submittedReportText
                    : reportStatus === reportStatusUnderReview
                      ? reviewReportText
                      : reportStatus === reportStatusRejected
                        ? rejectedReportText
                        : inProgressReportText}
            </span>
            <GoASpacer vSpacing="xs"></GoASpacer>
            <div className="dashboard-report-button-group">
              {!isArchived && isReportApproved && (
                <>
                  <GoAButton
                    type="tertiary"
                    trailingIcon="open"
                    onClick={() => handleAccessReportClick()}
                  >
                    Access report
                  </GoAButton>
                </>
              )}

              {isArchived && isReportApproved && (
                <GoAButton
                  type="primary"
                  trailingIcon="open"
                  onClick={() => handleAccessReportClick()}
                >
                  Access report
                </GoAButton>
              )}

              {!isArchived && reportStatus == reportStatusInProgress && (
                <>
                  <GoAButton
                    type="primary"
                    onClick={() => handleResumeButtonClick()}
                  >
                    Resume report
                  </GoAButton>
                  <GoAButton
                    type="tertiary"
                    onClick={() => navigate(navigateToReviewStep)}
                  >
                    View report summary
                  </GoAButton>
                </>
              )}
            </div>
          </div>
        </GoABlock>
      </GoAContainer>
    </div>
  );
};
